import {
  createContext, useCallback, useContext, useEffect, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from './primitives';

// ─── Modal ───────────────────────────────────────────────────────────────────

/**
 * Modal — frosted overlay (blurred, dimmed) with a floating card: 16px radius,
 * the large layered shadow, generous padding.
 */
export function Modal({
  isOpen, onClose, title, children, footer, size = 'md',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    // Prevent the page behind from scrolling while the modal owns the viewport.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[8vh] backdrop-blur-[8px] animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'card w-full !rounded-xl shadow-lg animate-scale-in',
          size === 'sm' && 'max-w-md',
          size === 'md' && 'max-w-xl',
          size === 'lg' && 'max-w-3xl',
          size === 'xl' && 'max-w-5xl',
        )}
      >
        <div className="flex items-center justify-between gap-4 border-b border-rule px-8 py-5">
          <h2 className="section-title">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-md text-xl leading-none text-ink-tertiary transition-colors hover:bg-surface-active hover:text-ink"
          >
            ×
          </button>
        </div>
        <div className="px-8 py-6">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-rule px-8 py-5">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  isOpen, onClose, onConfirm, title, message, confirmLabel = 'Confirm', isDestructive,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmLabel?: string;
  isDestructive?: boolean;
}) {
  const [isBusy, setIsBusy] = useState(false);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={isDestructive ? 'destructive' : 'primary'}
            isLoading={isBusy}
            onClick={async () => {
              setIsBusy(true);
              try {
                await onConfirm();
                onClose();
              } finally {
                setIsBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-body text-ink-secondary">{message}</p>
    </Modal>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────

type ToastTone = 'info' | 'success' | 'error';
interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
}

const ToastContext = createContext<{
  toast: (message: string, tone?: ToastTone) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, message, tone }]);
    // Errors linger — the user may need to read a validation message twice.
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 7000 : 4000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className="fixed bottom-5 right-5 z-[60] flex w-80 flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={clsx(
                'card relative overflow-hidden !shadow-lg animate-slide-down border-l-[3px] px-4 py-3 pb-3.5 text-body',
                t.tone === 'success' && 'border-l-positive',
                t.tone === 'error' && 'border-l-negative',
                t.tone === 'info' && 'border-l-secondary',
              )}
            >
              {t.message}
              {/* Auto-dismiss progress — drains over the toast's lifetime. */}
              <span
                aria-hidden
                className={clsx(
                  'absolute bottom-0 left-0 h-0.5',
                  t.tone === 'success' && 'bg-positive/50',
                  t.tone === 'error' && 'bg-negative/50',
                  t.tone === 'info' && 'bg-secondary/50',
                )}
                style={{ animation: `toast-progress ${t.tone === 'error' ? 7000 : 4000}ms linear forwards` }}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx.toast;
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

export function Tabs({
  tabs, active, onChange,
}: {
  tabs: Array<{ key: string; label: string; count?: number }>;
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="rule mb-section -mx-page-sm overflow-x-auto px-page-sm md:mx-0 md:px-0">
      <div className="flex min-w-max gap-5">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            aria-current={active === tab.key}
            className={clsx(
              'whitespace-nowrap pb-2.5 font-sans text-body-sm font-medium transition-colors duration-fast',
              active === tab.key
                ? 'border-b-2 border-secondary text-secondary'
                : 'border-b-2 border-transparent text-ink-secondary hover:text-ink',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1.5 tabular text-ink-tertiary">{tab.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Breadcrumb ──────────────────────────────────────────────────────────────

export function Breadcrumb({ items }: { items: Array<{ label: string; href?: string }> }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-1.5 font-sans text-caption">
      {items.map((item, i) => (
        <span key={`${item.label}-${i}`} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-ink-tertiary">/</span>}
          {item.href && i < items.length - 1 ? (
            <Link to={item.href} className="text-ink-tertiary transition-colors hover:text-ink">
              {item.label}
            </Link>
          ) : (
            <span className={i === items.length - 1 ? 'text-ink-secondary' : 'text-ink-tertiary'}>
              {item.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
